import React, { useContext, useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FiHome, FiTruck, FiMap, FiAlertTriangle, FiBarChart2, FiUsers, FiLogOut } from 'react-icons/fi';
import AuthContext from '../../context/AuthContext';

const Sidebar = ({ isExpanded = false }) => {
  const { hasRole, logout } = useContext(AuthContext);
  const [userRole, setUserRole] = useState('admin'); // Default to admin for safety
  const [userName, setUserName] = useState('User');
  const navigate = useNavigate();
  
  // Get user role from localStorage on component mount
  useEffect(() => {
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const userData = JSON.parse(userJson);
        setUserRole(userData.role || 'admin');
        setUserName(userData.name || 'User');
        console.log('Sidebar: Using role from localStorage:', userData.role);
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
    }
  }, []);
  
  // Local implementation of hasRole to avoid dependency on context
  const checkRole = (roles) => {
    // Always check localStorage first
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const userData = JSON.parse(userJson);
        if (roles.includes(userData.role)) {
          return true;
        }
      }
    } catch (error) {
      console.error('Error in checkRole:', error);
    }
    
    // Fallback to context hasRole
    return hasRole(roles);
  };
  
  // Handle logout click
  const handleLogout = () => {
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    
    // Trigger context logout if available
    if (logout) {
      logout();
    }
    
    // Navigate to login
    navigate('/auth/login');
  };
  
  const navItems = [
    { 
      to: '/', 
      name: 'Dashboard', 
      icon: <FiHome size={20} />,
      roles: ['admin', 'fleet_manager', 'driver']
    },
    { 
      to: '/vehicles', 
      name: 'Vehicles', 
      icon: <FiTruck size={20} />,
      roles: ['admin', 'fleet_manager', 'driver']
    },
    { 
      to: '/geofences', 
      name: 'Geofences', 
      icon: <FiMap size={20} />,
      roles: ['admin', 'fleet_manager']
    },
    { 
      to: '/alerts', 
      name: 'Alerts', 
      icon: <FiAlertTriangle size={20} />,
      roles: ['admin', 'fleet_manager']
    },
    { 
      to: '/reports', 
      name: 'Reports', 
      icon: <FiBarChart2 size={20} />,
      roles: ['admin', 'fleet_manager']
    },
    { 
      to: '/users', 
      name: 'Users', 
      icon: <FiUsers size={20} />,
      roles: ['admin']
    }
  ];
  
  // Filter nav items by role - use local implementation
  const filteredNavItems = navItems.filter(item => checkRole(item.roles));
  
  return (
    <div className="bg-primary text-white w-full h-full flex-shrink-0 flex flex-col">
      {/* Logo */}
      <div className="h-16 flex items-center justify-center shadow-md overflow-hidden">
        {isExpanded ? (
          <h1 className="text-xl font-bold">Fleet Tracker</h1>
        ) : (
          <h1 className="text-xl font-bold">FT</h1>
        )}
      </div>
      
      {/* Nav items */}
      <nav className="mt-4 flex-1 overflow-y-auto">
        <ul>
          {filteredNavItems.map((item, index) => (
            <li key={index}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center ${isExpanded ? 'space-x-3 px-4' : 'justify-center'} py-3 ${
                    isActive ? 'bg-primary-dark text-white' : 'text-white hover:bg-primary-dark'
                  }`
                }
                end={item.to === '/'}
                title={!isExpanded ? item.name : undefined}
              >
                <div className={isExpanded ? '' : 'p-1'}>
                  {item.icon}
                </div>
                {isExpanded && <span>{item.name}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      
      {/* User info at bottom */}
      <div className="mt-auto bg-primary-dark text-sm">
        {isExpanded ? (
          <div className="p-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <FiUsers size={16} />
              </div>
              <div className="flex-1">
                <div className="font-medium">{userName}</div>
                <div className="text-xs text-white/60">{userRole ? userRole.replace('_', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'User'}</div>
              </div>
              <div 
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center cursor-pointer hover:bg-white/20" 
                title="Logout"
                onClick={handleLogout}
              >
                <FiLogOut size={16} />
              </div>
            </div>
          </div>
        ) : (
          <div className="py-4 flex flex-col items-center">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center mb-3">
              <FiUsers size={16} />
            </div>
            <div 
              className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center cursor-pointer hover:bg-white/20" 
              title="Logout"
              onClick={handleLogout}
            >
              <FiLogOut size={16} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;